import type { CheckboxProps as RadixCheckboxProps } from "@radix-ui/react-checkbox";
import * as RadixCheckbox from "@radix-ui/react-checkbox";

import clsx from "clsx";
import { forwardRef, ReactNode } from "react";
import { Icon } from "../Icon";

type CheckboxBaseProps = {
  label?: ReactNode;
  error?: string;
} & Omit<RadixCheckboxProps, "asChild" | "name" | "id">;

type CheckboxProps = CheckboxBaseProps &
  ({ name: string; id?: string } | { id: string; name?: string });

const container = {
  base: "h-6 w-6 cursor-pointer rounded-sm flex items-center disabled:bg-gray-300 disabled:pointer-events-none active:scale-95 justify-center border focus-visible:outline-none focus-visible:ring-2 ring-offset-2 ring-theme-accent transition duration-150 ease-in-out",
  state: {
    default: "bg-white hover:bg-gray-300 border-default",
    checked: "bg-theme-accent text-white border-theme-accent",
    indeterminate: "bg-theme-accent text-white border-theme-accent",
  },
};

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      checked,
      onCheckedChange,
      disabled,
      required,
      name,
      id: idProp,
      value,
      label,
      error,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
    },
    ref,
  ) => {
    const id = idProp ?? name;
    return (
      <div className="flex flex-row space-x-2">
        <RadixCheckbox.Root
          checked={checked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          required={required}
          name={name}
          id={id}
          value={value}
          ref={ref}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          className={clsx([
            container.base,
            checked === "indeterminate"
              ? container.state.indeterminate
              : checked
                ? container.state.checked
                : container.state.default,
          ])}
        >
          <RadixCheckbox.Indicator className="flex items-center justify-center">
            {checked === "indeterminate" ? (
              <Icon icon="IndeterminateSolid" className="text-sm text-white" />
            ) : (
              checked && (
                <Icon icon="CheckSolid" className="text-base text-white" />
              )
            )}
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>
        <div>
          {label && (
            <label
              className="text-gray-1200 mb-1 block text-base font-medium cursor-pointer"
              htmlFor={id}
            >
              {label}
            </label>
          )}
          {error && (
            <span className="text-signal-error mt-0.5 block text-sm font-medium">
              {error}
            </span>
          )}
        </div>
      </div>
    );
  },
);

Checkbox.displayName = "Checkbox";
